import type { ColorParam, LandscapingConfig } from './types';

export const DEFAULT_MASTER_PROMPT = `1. ABSOLUTE COLOR RESET & FAÇADE REPAINT MANDATE
- Complete Paint Overhaul: You MUST completely wipe away, erase, and replace ALL previous paint colors, previous renderings, old unpainted stucco, commercial shopfronts, and old color combinations (such as Abacadabra, previous whites/greys/pinks/beige, or raw concrete).
- ZERO Color Mixing or Old Signage: Remove all commercial shop signboards, shop banners, billboard posters, and storefront clutter. Apply ONLY the newly assigned 3-color scheme ([WALL_NAME] [WALL_HEX], [BORDER_NAME] [BORDER_HEX], [HIGHLIGHT_NAME] [HIGHLIGHT_HEX]).

2. 100% COLOR FIDELITY & FAÇADE COVERAGE DIRECTIVE
- Primary Wall 100% Facade Coverage: Map WALL_HEX ([WALL_HEX], [WALL_NAME]) across ALL primary exterior wall surfaces, upper floor elevation walls, balcony backwalls, top floor arch walls, side facades, and outer stucco planes without exception.
- Light Shade Saturation: When [WALL_NAME] ([WALL_HEX]) is a light shade (e.g., Abacadabra #CEC9C3, Warm Sand #E8C59C, Air Cushion #EAE6DF, Alabaster #F4F0E8, Alps Blue #B8D5E5, Soft Yellow #FACF19, Cream, Beige, Ivory, Off-White), you MUST distinctly tint and coat EVERY primary exterior wall plane so the exact hue of [WALL_HEX] is 100% clearly visible under natural daylight.
- BORDER_HEX Assignment: Map BORDER_HEX ([BORDER_HEX], [BORDER_NAME]) (e.g., Slate Graphite #3A404A) to painted pillars, window trim borders, arch surrounds, roof eaves, column bands, and edge trims ONLY.
- HIGHLIGHT_HEX Assignment: Map HIGHLIGHT_HEX ([HIGHLIGHT_HEX], [HIGHLIGHT_NAME]) (e.g., Absolute Yellow #FACF19) to architectural feature walls, balcony recesses, arch insets, or accent elevation sections.

3. MANDATORY ELECTRIC POLE, WIRE & CABLE REMOVAL DIRECTIVE
- Complete Wire & Pole Erasure: You MUST completely erase, wipe away, and remove ALL electric utility poles, wooden power posts, transformer boxes, street light poles, overhead black wire bundles, and hanging electrical cables in front of or around the building.
- Clean Unobstructed View: The entire foreground space in front of the building MUST BE 100% CLEAR OF ALL POLES, WIRES, AND OVERHEAD CABLES. The sky and building facade must be completely unobstructed.

4. MINIMAL & LOW-PROFILE LANDSCAPING DIRECTIVE (SHOW ALL WALL COLORS)
- Minimal Shrubs Only: Landscaping MUST be minimal and low-profile. Use ONLY small shrubs, low boxwood hedges, and neat ground grass beds below knee height.
- NO Large Trees or High Foliage: ABSOLUTELY NO large trees, tall palms, or dense high foliage in front of the house that block the building facade. The entire painted wall facade, ground floor, and entrance MUST remain 100% unobstructed and visible.

5. MANDATORY BLACK RAILINGS & BLACK GATES DIRECTIVE
- Railings Rule (100% Black): ALL balcony railings, terrace parapet railings, stair railings, and window guard grills MUST be finished in sleek, matte/semi-gloss **BLACK COLOR** (#000000 / #1A1A1A). Do NOT paint railings in the wall, border, or highlight color.
- Gates Rule (100% Black - Excluded from Border): ALL main entrance gates, perimeter driveway gates, and steel security gates MUST be finished in sleek **BLACK COLOR** (#000000 / #1F1F1F). Gates MUST NOT be painted in the BORDER color.

6. STRUCTURE & CLEANUP DIRECTIVE
- Scaffolding & Debris Erasure: Completely remove metal scaffolding, bamboo framing, ladders, utility clutter, construction materials, and foreground clutter.
- Surface Normalization: Convert raw concrete blocks, brickwork, or un-plastered walls into smooth exterior painted surfaces while retaining all architectural arches, balcony projections, and structural pillars.

7. SUBJECT ISOLATION & NEIGHBORING BUILDINGS DIRECTIVE
- Single House Focus: Focus exclusively on the target main building in the foreground/center.
- Do NOT render or include portions of adjacent neighboring houses on the left or right side. Keep adjacent properties strictly outside the focal frame or as plain unpainted neutral background.

8. FENESTRATION & GLASS RULE
- Staircase & Window Glass: Apply clear or reflective architectural glass panes with sleek finished frames on all window openings and balconies.

9. COLOUR FIDELITY ENFORCEMENT
- Enforce strict 100% color accuracy matching the supplied HEX codes ([WALL_HEX], [BORDER_HEX], [HIGHLIGHT_HEX]).
- Apply paint textures as durable, semi-gloss/matte exterior finishes under natural daylight conditions. Maintain realistic depth, natural shadows, ambient occlusion, and sun highlights without shifting the underlying hues.`;

export function generateFullPrompt(
  wall: ColorParam,
  border: ColorParam,
  highlight: ColorParam,
  masterPromptTemplate: string = DEFAULT_MASTER_PROMPT,
  landscaping?: LandscapingConfig,
): string {
  let prompt = masterPromptTemplate;

  // Replace variable placeholders
  prompt = prompt.replace(/\[WALL_NAME\]/g, wall.name);
  prompt = prompt.replace(/\[WALL_HEX\]/g, wall.hex);
  prompt = prompt.replace(/\[WALL_RGB\]/g, wall.rgb);
  prompt = prompt.replace(/\[WALL_TYPE\]/g, wall.type);

  prompt = prompt.replace(/\[BORDER_NAME\]/g, border.name);
  prompt = prompt.replace(/\[BORDER_HEX\]/g, border.hex);
  prompt = prompt.replace(/\[BORDER_RGB\]/g, border.rgb);
  prompt = prompt.replace(/\[BORDER_TYPE\]/g, border.type);

  prompt = prompt.replace(/\[HIGHLIGHT_NAME\]/g, highlight.name);
  prompt = prompt.replace(/\[HIGHLIGHT_HEX\]/g, highlight.hex);
  prompt = prompt.replace(/\[HIGHLIGHT_RGB\]/g, highlight.rgb);
  prompt = prompt.replace(/\[HIGHLIGHT_TYPE\]/g, highlight.type);

  let landscapingPromptSnippet = '';
  if (landscaping) {
    const plantList =
      landscaping.plants && landscaping.plants.length > 0
        ? landscaping.plants.join(', ')
        : 'Areca Palms, Bougainvillea, Jasmine shrubs';
    landscapingPromptSnippet = `
CUSTOM LANDSCAPING DIRECTIVE:
- Landscaping Preset: ${landscaping.presetName || 'Tropical Indian Garden'}
- Selected Native Plants & Flora: ${plantList}
- Lawn & Turf Style: ${landscaping.lawnStyle || 'Manicured Emerald Lawn'}
- Walkway & Pathway Style: ${landscaping.pathwayStyle || 'Interlocking Stone Paver Walkway'}
${landscaping.customNotes ? `- Custom Landscaping Note: ${landscaping.customNotes}` : ''}`;
  }

  const variablesHeader = `CONTEXT & MISSION:
You are the core rendering engine for an exterior architectural makeover platform specialized in Indian subcontinent building architecture. You will execute a complete structural cleanup, normalize finishes, preserve the original building architecture with 100% geometric accuracy, and synthesize exact paint combinations.

COLOR PARAMETERS (100% EXACT MATCH REQUIRED):
- WALL (Primary Surface): Name: ${wall.name}, HEX: ${wall.hex}, RGB: (${wall.rgb}), Type: ${wall.type}
- BORDER (Trim & Columns ONLY): Name: ${border.name}, HEX: ${border.hex}, RGB: (${border.rgb}), Type: ${border.type} (NOTE: Do NOT apply border paint to gates or railings!)
- HIGHLIGHT (Accent Features): Name: ${highlight.name}, HEX: ${highlight.hex}, RGB: (${highlight.rgb}), Type: ${highlight.type}

CRITICAL FIXATION RULES (MANDATORY):
- RAILINGS COLOR: ALL balcony, terrace, staircase, and window railings MUST BE 100% BLACK (#000000).
- GATES COLOR: ALL entrance gates, perimeter steel gates, and driveway gates MUST BE 100% BLACK (#000000) and MUST NOT BE CONSIDERED PART OF OR PAINTED IN BORDER COLOR.
${landscapingPromptSnippet}

MASTER PROMPT DIRECTIVES:
${prompt}

OUTPUT DIRECTION:
Generate a high-resolution, photorealistic exterior render of this exact house building structure, 100% identical replica of the uploaded image's architectural geometry, cleaned of all clutter/scaffolding/unplastered walls, with all railings and gates rendered strictly in black color, and accurately painted according to the assigned color directives.`;

  return variablesHeader;
}
